import type { CategoryContent } from '../../row.js';

/** Kunst, muziek en beeldmedia, tweede reeks. Juiste antwoord staat eerst; wordt bij het bouwen gerandomiseerd. */

export const MORE_ART: CategoryContent = {
  msc: [
    [
      'Wat is chiaroscuro?',
      [
        'Het modelleren van vorm door sterke contrasten van licht en donker',
        'Het onmerkbaar in elkaar laten overvloeien van tinten',
        'Het aanbrengen van verf in dikke, texturele streken',
        'De ondertekening die zichtbaar is onder een dunne verflaag',
      ],
      0,
      "Sfumato is het vervloeien, impasto de dikke toepassing. Caravaggio's extreme versie wordt soms apart aangeduid als tenebrisme.",
    ],
    [
      'Wat beschrijft de term "contrapposto"?',
      [
        'Een figuur die het gewicht op één been laat rusten, met tegengestelde rotatie door de romp als gevolg',
        'Twee figuren gespiegeld opgesteld rond een centrale as',
        'Een compositie opgebouwd rond tegengestelde diagonalen',
        'Een houding waarin het hoofd tegen de bewegingsrichting in draait',
      ],
      0,
      'De Doryphoros is het klassieke voorbeeld, en de herontdekking daarvan is een groot deel van wat "klassiek" voor de Renaissance betekende.',
    ],
    [
      'Het Arnolfini-portret is van wie?',
      [
        'Jan van Eyck',
        'Rogier van der Weyden',
        'Robert Campin, de Meester van Flémalle',
        'Hans Memling',
      ],
      0,
      "1434. Panofsky's lezing van de objecten erin als verhulde symboliek is even beroemd als het schilderij zelf, en wordt nu breed genuanceerd.",
    ],
    [
      'Wat is een "edicule" in de architectuur?',
      [
        'Een omkaderde nis of opening geflankeerd door kolommen en bekroond met een fronton',
        'Een klein koepelbouwsel opgetrokken boven een viering',
        'Een verdiept paneel in een gewelf of plafond',
        'Een uitstekende bovenverdieping gedragen door consoles',
      ],
      0,
      'Het verdiepte paneel is een cassette. Edicules zijn de manier waarop de klassieke architectuur van een muuropening een gebeurtenis maakt.',
    ],
    [
      'Waar verwijst japonisme naar?',
      [
        'De invloed van Japanse kunst op Europese schilderkunst en vormgeving na de jaren 1850',
        'De Japanse omgang met westerse olieverfschilderkunst in de Meiji-periode',
        'Een negentiende-eeuwse Europese mode voor Chinees en Japans porselein',
        'De school van Japanse prentmakers die voor de exportmarkt werkten',
      ],
      0,
      'De compositie van ukiyo-e - bijsnijden, platheid, hoge gezichtspunten - is rechtstreeks terug te zien bij Degas, Whistler en Van Gogh.',
    ],
    [
      'Wat is het kenmerkende van gotische ribgewelven?',
      [
        'De belasting wordt geconcentreerd op ribben en zo naar de pijlers afgevoerd, waardoor het gewelfvlak en de muur worden ontlast',
        'Het gewelf wordt zonder bekisting opgetrokken, in zelfdragende lagen',
        'Het profiel van het gewelf is een echte kettinglijn',
        'Het gewelf is volledig van gegoten beton',
      ],
      0,
      'Het concentreren van de stuwkracht is wat de raamoppervlakte mogelijk maakt, wat eigenlijk het hele punt van het systeem is.',
    ],
    [
      'De Wiener Secession werd opgericht als reactie op wat?',
      [
        'Het conservatisme van het Künstlerhaus en het academische establishment',
        'Het Bauhaus-programma om kunst en industrie samen te brengen',
        'De dominantie van het Franse impressionisme in Oostenrijkse collecties',
        'De weigering van de staat om openbare opdrachten voor levende kunstenaars te financieren',
      ],
      0,
      'Opgericht in 1897 met Klimt als lid, gaat de Secession het Bauhaus twee decennia vooraf. De twee bewegingen hebben elkaar in werkelijkheid dus nooit overlapt, hoewel beide vaak worden aangehaald als mijlpalen van dezelfde bredere afkeer van de academische kunst.',
    ],
    [
      'Wat is een "carton" in de context van fresco- of tapijtproductie?',
      [
        'Een voorbereidende tekening op ware grootte, overgebracht op het werkoppervlak',
        'Een satirische tekening gepubliceerd in de periodieke pers',
        'Een kleinschalige kleurstudie gemaakt vóór het volledige ontwerp',
        'Een patronenboek met standaardmotieven bewaard in een werkplaats',
      ],
      0,
      'Van cartone, dik papier. De tapijtcartons van Rafaël zijn bewaard gebleven, en de tapijten die daarvan geweven werden zijn de kopieën - wat de gebruikelijke hiërarchie omdraait.',
    ],
    [
      'De term "readymade" wordt geassocieerd met wie?',
      [
        'Marcel Duchamp',
        'Kurt Schwitters, in zijn Merz-assemblages',
        'Man Ray, in zijn fotografische objecten',
        'Jean Arp, in zijn toevalscollages',
      ],
      0,
      'Fountain, 1917, en de discussie die het losmaakte over of het gebaar of het object het werk is.',
    ],
    [
      'Wat bestudeert "iconografie" in de zin van Panofsky?',
      [
        'De identificatie van het onderwerp en de conventionele betekenissen daarvan',
        'De formele analyse van compositie los van het onderwerp',
        'De materiële en technische totstandkoming van het object',
        'De diepere culturele symptomen die een werk onbewust uitdrukt',
      ],
      0,
      'Dat laatste is iconologie, zijn derde en meest ambitieuze niveau - en het niveau dat het vaakst wordt samengevoegd met het tweede.',
    ],
  ],
  phd: [
    [
      'Wat is het "period eye" zoals Baxandall het gebruikt?',
      [
        'De cultureel getrainde visuele vaardigheden die een tijdgenoot meebracht bij het bekijken van een schilderij',
        'Het karakteristieke gezichtspunt van waaruit een fresco bedoeld was te worden bekeken',
        'De optische vertekeningen geïntroduceerd door vroege op lenzen gebaseerde tekenhulpmiddelen',
        'Het conventionele palet dat schilders in een bepaald decennium ter beschikking stond',
      ],
      0,
      'Het meten van vaten en het dansen leerde vijftiende-eeuwse kijkers volume en verhouding te lezen - een sociale geschiedenis van vaardigheden in plaats van smaak.',
    ],
    [
      'Wat probeerde Riegls begrip "Kunstwollen" te bewerkstelligen?',
      [
        'Stijlverandering verklaren als gerichte artistieke wil in plaats van als verval of technisch falen',
        'Een objectieve maatstaf voor esthetische kwaliteit over periodes heen vaststellen',
        'Stijlontwikkeling vooral herleiden tot veranderingen in beschikbare materialen, pigmenten en dragers',
        'Stijlverandering toeschrijven aan de psychologie van individuele meesters',
      ],
      0,
      'Het rehabiliteerde de laat-Romeinse kunst, die het Renaissance-model alleen als verval kon lezen.',
    ],
    [
      'Wat betekent "disegno" in de zestiende-eeuwse Italiaanse kunsttheorie?',
      [
        'Ontwerp als intellectuele conceptie, beschouwd als de grondslag van alle kunsten',
        'De techniek van tekenen met zilverstift op geprepareerd papier',
        'Het contract dat het onderwerp en de materialen van een opdracht vastlegt',
        'Het proportionele raster dat gebruikt wordt om een ontwerp op een muur over te brengen',
      ],
      0,
      "Vasari's centrale term, en de basis van de Florentijnse claim dat schilderkunst een vrije kunst is in plaats van een ambacht.",
    ],
    [
      'Wat is de technische kern van het debat over de "camera obscura" rond Vermeer?',
      [
        'Of optische hulpmiddelen bepaalde beeldeffecten verklaren',
        'Of Vermeer een lens gebruikte om zijn eigen pigmenten te malen',
        'Of de schilderijen als projecties op glas gemaakt werden',
        'Of zijn palet alleen te verklaren is door optische mengingen',
      ],
      0,
      'Steadman en Wheelock voeren het debat; de interessante vraag is niet of hij het kon hebben gedaan, maar wat de "verwarringscirkels" eigenlijk bewijzen.',
    ],
    [
      'Waar draaide het theologische argument van de Byzantijnse iconoclasme om?',
      [
        'Of een afbeelding van Christus noodzakelijkerwijs zijn twee naturen verdeelt of verwart',
        'Of afbeeldingen in privé wel maar in het openbare gebed niet vereerd konden worden',
        'Of het tweede gebod überhaupt op christenen van toepassing was',
        'Of alleen relieken, niet afbeeldingen, de goddelijke aanwezigheid konden bemiddelen',
      ],
      0,
      'Johannes van Damascus en later Nikephoros beantwoorden het via de incarnatie - dat Christus afbeeldbaar is, volgt uit het feit dat hij gezien is.',
    ],
    [
      'Wat betekent "plaatsgebondenheid" in de sculptuur na 1960, en waar wringt de discussie?',
      [
        'Een werk dat door zijn locatie wordt geconstitueerd',
        'Een werk ontworpen om zonder aanpassing op elke locatie te kunnen worden gereproduceerd',
        'Een werk gemaakt uit materialen gevonden op de plaats van installatie',
        'Een werk waarvan de betekenis afhangt van de identiteit van de opdrachtgever',
      ],
      0,
      'Serra verloor, en de verwijdering is nu de standaardcasus voor wat de claim juridisch én kritisch precies inhoudt.',
    ],
    [
      'Wat is de "Nazarener"-beweging, en waarom is die van belang voor de negentiende-eeuwse schilderkunst?',
      [
        'Een Duits broederschap dat pre-Rafaëlitische Italiaanse modellen deed herleven',
        'Een Franse academische school die het Davidiaanse neoclassicisme verdedigde',
        'Een Italiaanse revival van Byzantijnse icoontechnieken',
        'Een Russische groep realistische schilders die zich afsplitste van de Academie',
      ],
      0,
      'De Lukasbund in Rome, vanaf 1809. De Peredvizjniki zijn de Russische afsplitsing, en een apart verhaal.',
    ],
    [
      'Waarin verschillen Alois Riegl en Wölfflin voornamelijk?',
      [
        'Riegl verklaart stijl door gerichte wil, Wölfflin door terugkerende formele polariteiten',
        'Riegl bestudeert alleen schilderkunst, Wölfflin alleen architectuur',
        'Riegl richt zich op opdrachtgeverschap, Wölfflin op techniek',
        'Riegl steunt op kennerschap, Wölfflin op documentair bewijs',
      ],
      0,
      "Wölfflins vijf begrippenparen - lineair versus schilderachtig en zo verder - zijn een beschrijvend apparaat, geen causale verklaring, en dat is precies de kritiek erop.",
    ],
    [
      'Wat deed de Münchense tentoonstelling "Entartete Kunst" van 1937?',
      [
        'Toonde geconfisqueerde moderne werken met vijandige onderschriften om ze publiekelijk in diskrediet te brengen',
        'Presenteerde door de staat goedgekeurde kunst als voorbeeld voor Duitse schilders',
        'Verkocht geconfisqueerde werken in het buitenland om buitenlandse valuta op te halen voor de bewapening',
        'Documenteerde werken die vernietigd waren tijdens de voorafgaande zuivering van musea',
      ],
      0,
      'Ze trok veel meer bezoekers dan de goedgekeurde tentoonstelling die er tegelijk werd gehouden, wat de organisatoren niet aan de grote klok hingen.',
    ],
    [
      'Op welk probleem berustte de "Vitruviaanse" traditie in de Renaissance-architectuur?',
      [
        'De tekst van Vitruvius is bewaard gebleven zonder de bijbehorende illustraties',
        'Vitruvius was tot de zestiende eeuw alleen bekend in Arabische vertaling',
        'De tekst werd aan Vitruvius toegeschreven maar eeuwen later geschreven',
        'Vitruvius beschreef doorlopend Griekse in plaats van Romeinse praktijk',
      ],
      0,
      'Daardoor verschillen Alberti, Serlio en Palladio allemaal in hun opvatting over de orden, terwijl ze allemaal dezelfde autoriteit claimen.',
    ],
  ],
  professor: [
    [
      'Wat is de methodologische beperking van Wölfflin zoals latere critici die aanwijzen?',
      [
        'Zijn formele polariteiten beschrijven verandering zonder te verklaren waarom die optreedt',
        'Zijn categorieën gelden alleen voor de Italiaanse kunst van twee eeuwen',
        'Hij vertrouwde op reproducties in plaats van directe observatie',
        'Hij verwierp toeschrijvingsvragen als irrelevant voor stijl',
      ],
      0,
      'Vergelijking zonder causaliteit. Precies dat gat is waar de sociale kunstgeschiedenis naar toe bewoog.',
    ],
    [
      'Wat stelt Krauss\' "sculpture in the expanded field" voor?',
      [
        'Een structurele plattegrond van de postmoderne praktijk waarin sculptuur wordt gedefinieerd door wat ze niet is',
        'Dat sculptuur begrepen moet worden via de fenomenologie van het lichaam van de toeschouwer',
        'Dat het minimalisme de mogelijkheden van het vrijstaande object heeft uitgeput',
        'Dat landart een apart medium met een eigen geschiedenis vormt',
      ],
      0,
      'Uit 1979, en het Klein-groepdiagram is het onderdeel dat iedereen zich herinnert. Het formalisme erin is ook wat critici aanvallen.',
    ],
    [
      'Waardoor onderscheidt de methode van het "Warburg Institute" zich?',
      [
        'Het volgen van de migratie van visuele formules en hun emotionele lading door periodes en media heen',
        'De systematische fotografische documentatie van een nationaal artistiek erfgoed',
        'De reconstructie van verloren werken op basis van documentair bewijs',
        'De toeschrijving van anonieme werken door kennerschap',
      ],
      0,
      'Warburgs bibliotheek was gerangschikt naar "de wet van de goede buur" - de plankvolgorde was zelf al het argument.',
    ],
    [
      'Wat is het belang van macro-XRF-scanning in de technische kunstgeschiedenis?',
      [
        'Elementgekarteerde beelden onthullen niet-destructief onderschilderingen en pigmentverdeling over een heel doek',
        'Het dateert een schilderij absoluut op basis van het verval van pigment-isotopen',
        'Het identificeert de werkplaats van herkomst alleen al aan de doekweving',
        'Het meet craquelurepatronen om latere restauraties te onderscheiden van de oorspronkelijke verf',
      ],
      0,
      'Weefselvergelijking is een aparte, echte techniek. XRF is wat "er zit iets onder" veranderde in een leesbaar beeld.',
    ],
    [
      'Wat duidt "het optisch onbewuste" aan in de theorie van Krauss?',
      [
        'Een verdrongen, lichamelijke stroming in het modernisme tegenover de lichaamloze opticaliteit die Greenberg voorstond',
        'De onwillekeurige nabeelden waarvan de op-art gebruikmaakt, en de perceptuele instabiliteit die ze bij de kijker teweegbrengen',
        'De onbewuste invloed van fotografie op schilderscompositie',
        'De perceptuele mechanismen die door de Gestaltpsychologie worden bestudeerd',
      ],
      0,
      'Het is evenveel een discussie met het modernisme van Greenberg als een geschiedenis ervan, wat een treffende omschrijving is van veel van haar werk.',
    ],
    [
      'Waarover gaat het debat over de Getty kouros?',
      [
        'Of stilistische afwijking of wetenschappelijke oppervlakteanalyse een authenticiteitsvraag moet beslechten',
        'Of Griekse marmeren beeldhouwkunst oorspronkelijk beschilderd was, en hoe bewaarde pigmentresten gelezen moeten worden',
        'Of de figuur een grafmonument of een votiefgift was',
        'Of het type afstamt van Egyptische of Nabije-Oosterse voorbeelden',
      ],
      0,
      'Dolomietverwering kan vervalst worden en kenners waren het oneens. Het museum gaf het uiteindelijk als omschrijving "circa 530 v.Chr. of moderne vervalsing", een ongewoon eerlijke catalogustekst.',
    ],
    [
      'Wat is "de beweeglijkheid van het beeldvlak" in beschrijvingen van islamitisch ornament?',
      [
        'Het patroon weigert één vast gezichtspunt',
        'Ornament was ontworpen om vanuit een bewegende processie bekeken te worden',
        'Tegels werden periodiek herschikt om het uiterlijk van een ruimte te veranderen',
        'Geometrisch patroon codeert een bewust ambigu perspectief',
      ],
      0,
      'Het werk van Grabar en later Necipoğlu hierover heeft de oudere lezing van ornament als horror vacui verdrongen.',
    ],
    [
      'Wat beoogde de voorbereidende cursus (Vorkurs) van het Bauhaus onder Itten?',
      [
        'Studenten ontdoen van aangeleerde conventies vóór enige ambachtelijke training',
        'Tekenvaardigheid tot een gemeenschappelijk niveau brengen vóór indeling in werkplaatsen',
        'Theoretisch onderwijs geven in kleurenleer en perspectief',
        'De aanleg van studenten voor bepaalde industriële vakken testen',
      ],
      0,
      'Moholy-Nagy en Albers herwerkten hem nadat Itten vertrokken was. Zijn nazaten zitten in elk basisjaar dat nu nog draait.',
    ],
    [
      'Waardoor kenmerkt zich de "New Art History" van de jaren zeventig en tachtig?',
      [
        'Het importeren van marxistische, feministische en psychoanalytische methoden tegen kennerschap en formele analyse',
        'Een terugkeer naar archiefonderzoek van opdrachtgeverschap en werkplaatspraktijk',
        'De toepassing van statistische methoden op toeschrijvingsvraagstukken',
        'De verwerping van periodisering als ordenend principe, ten gunste van puur thematische indelingen',
      ],
      0,
      'Nochlins "Why Have There Been No Great Women Artists?" is het stuk dat laat zien wat die verschuiving werkelijk opleverde.',
    ],
    [
      'Welk probleem stelt het begrip "het origineel" voor de architectonische monumentenzorg?',
      [
        'De significante staat van een gebouw kan er meerdere zijn',
        'Oorspronkelijke materialen kunnen niet met voldoende precisie gedateerd worden',
        'Oorspronkelijke tekeningen zijn voor premoderne gebouwen zelden bewaard gebleven',
        'De oorspronkelijke constructieve werking kan niet worden geverifieerd zonder tot falen toe te testen',
      ],
      0,
      'Ruskin tegenover Viollet-le-Duc, nog altijd onopgelost, en de reden dat het Charter van Venetië leest als een reeks compromissen.',
    ],
  ],
};

export const MORE_MUSIC: CategoryContent = {
  msc: [
    [
      'Wat is de gebruikelijke functie van een Napolitaanse akkoord?',
      [
        'Een subdominant-achtig akkoord, meestal als opmaat naar de dominant',
        'Een tonica-vervanger aan het begin van een frase',
        'Een dominant-vervanger die direct naar de tonica oplost',
        'Een doorgangsakkoord tussen twee omkeringen van de tonica',
      ],
      0,
      'Gebouwd op de verlaagde tweede toontrap en meestal in eerste omkering, waardoor de vierde toontrap in de bas komt en de opmaat vloeiend wordt.',
    ],
    [
      'Wat is het antwoord (answer) in een fuga?',
      [
        'Het thema herhaald in een andere stem op de kwint of kwart',
        'Het contrasubject dat tegen de oorspronkelijke inzet wordt geïntroduceerd',
        'Het thema omgekeerd en in de bas gespeeld',
        'De afsluitende presentatie van het thema in de tonica',
      ],
      0,
      'Tonaal versus reëel antwoord - of intervallen worden aangepast om binnen de toonsoort te blijven - is het eerste waar een contrapuntoefening op wordt getoetst.',
    ],
    [
      'Wat hield de barokke affectenleer in?',
      [
        'Specifieke muzikale figuren wekken betrouwbaar specifieke emoties bij de luisteraar op',
        'Muziek moet de natuurlijke geluiden van de wereld die zij uitbeeldt navolgen',
        'Het eigen gevoel van de componist is het juiste onderwerp van het werk',
        'Emotionele expressie moet ondergeschikt zijn aan formele verhoudingen',
      ],
      0,
      'Een retorische in plaats van een romantische opvatting van expressie: de componist zet affecten in, hij biecht ze niet op.',
    ],
    [
      'Wat is een doorgangsnoot?',
      [
        'Een niet-akkoordtoon die de stapsgewijze afstand tussen twee harmonietonen invult',
        'Een akkoordtoon die aangehouden blijft terwijl de harmonie erom heen verandert',
        'Een dissonant die met een sprong wordt benaderd en stapsgewijs wordt opgelost',
        'Een versieringsnoot een halve toon boven de volgende harmonietoon',
      ],
      0,
      'De aangehouden toon is een voorhouding; de halve toon erboven is in de meeste gevallen een appoggiatura.',
    ],
    [
      'Welke componist schreef de "Fantasia on a Theme by Thomas Tallis"?',
      [
        'Ralph Vaughan Williams',
        'Gustav Holst, in hetzelfde decennium',
        'Edward Elgar, in zijn Introduction and Allegro',
        'Frederick Delius, in zijn Brigg Fair-variaties',
      ],
      0,
      '1910. Het dubbele strijkorkest en de modale harmoniek zijn allebei directe stellingnames over de Engelse muzikale afkomst.',
    ],
    [
      'Wat betekent "doorgecomponeerd" bij een lied?',
      [
        'Er klinkt doorlopend nieuwe muziek',
        'De begeleiding loopt door zonder rusten',
        'De tekst wordt syllabisch gezet zonder melisma',
        'Het lied moduleert zonder naar de tonica terug te keren',
      ],
      0,
      'Schuberts Erlkönig tegenover een strofisch gezette versie is de standaardvergelijking voor wat die keuze oplevert.',
    ],
    [
      'Met welke muziektraditie wordt de mbira dzavadzimu geassocieerd?',
      [
        'Sjona-ceremoniële muziek uit Zimbabwe',
        'Yoruba-trommelensembles uit Nigeria',
        'Mandé griot-repertoire uit de westelijke Sahel',
        'Ethiopisch liturgisch gezang van de orthodoxe kerk',
      ],
      0,
      'In elkaar grijpende cyclische patronen met een functie in geestbezetenheidsrituelen, en een sterke invloed op latere minimalistische ideeën over cycli.',
    ],
    [
      'Wat is een toonhoogtecluster?',
      [
        'Een akkoord van aangrenzende diatonische of chromatische tonen die tegelijk klinken',
        'Een snelle arpeggering over het volledige bereik van het instrument',
        'Een akkoord volledig opgebouwd uit gestapelde reine kwarten',
        'Een aangehouden groep boventonen opgewekt door lichte vingerdruk',
      ],
      0,
      'Cowell theoretiseerde ze en Ives gebruikte ze; de onderarm op de toetsen is de praktische instructie.',
    ],
    [
      'Wat is een tala in de Indiase klassieke muziek?',
      [
        'De metrische cyclus van een vast aantal tellen waarbinnen een uitvoering zich ontvouwt',
        'Het melodische kader van toegestane tonen en karakteristieke frasen',
        'De geïmproviseerde, ongemeten inleiding voorafgaand aan het stuk',
        'De drone die het hele stuk lang het toonaal centrum levert',
      ],
      0,
      'Het melodische kader is de raga, de inleiding de alap, de drone de tanpura - vier zaken die voortdurend met elkaar verward worden.',
    ],
    [
      'Wat doet een appoggiatura?',
      [
        'Klinkt als dissonant op de tel, en lost stapsgewijs op naar een akkoordtoon',
        'Anticipeert de volgende harmonie vóór de tel',
        'Vult een terts met een stapsgewijze doorgangsnoot',
        'Houdt een akkoordtoon aan over een harmoniewisseling heen',
      ],
      0,
      'Op de tel en met een sprong benaderd is de gangbare definitie, hoewel het achttiende-eeuwse gebruik losser is dan moderne handboeken suggereren.',
    ],
  ],
  phd: [
    [
      'Wat duidt de term "Klangfarbenmelodie" aan?',
      [
        'Een opeenvolging waarin klankkleur de structurele rol vervult die melodie normaal zou hebben',
        'Het verdubbelen van een melodische lijn over meerdere instrumenten voor kleur',
        'Een melodie afgeleid van de boventoonreeks van een grondtoon',
        'De orkestratie van een toetsinstrumentwerk voor volledig ensemble',
      ],
      0,
      "Schönbergs term uit de Harmonielehre van 1911; het derde van de Fünf Orchesterstücke is de demonstratie.",
    ],
    [
      'Wat is isoritmiek in motetten van de veertiende en vijftiende eeuw?',
      [
        'De herhaling van een ritmisch patroon in de tenor over opeenvolgende presentaties van de melodie',
        'De uitlijning van alle stemmen in identiek ritme gedurende het gehele stuk',
        'Het gebruik van één mensuratie in elk deel, zonder enige verhoudingswisseling daartussen',
        'De augmentatie van de tenor met een vaste verhouding bij elke inzet',
      ],
      0,
      'Talea en color, en het niet samenvallen daarvan genereert precies de grootschalige structuur van een motet van Machaut of Du Fay.',
    ],
    [
      'Waarover ging de "querelle des Bouffons"?',
      [
        'De relatieve verdiensten van de Franse en Italiaanse operastijl in het Parijs van het midden van de achttiende eeuw',
        'De aanvaardbaarheid van castraten in de kerkmuziek, en of de praktijk theologisch te verdedigen was',
        'De voorrang van tekst of muziek bij het op muziek zetten van dramatische tekst',
        'De toelating van instrumentale muziek tot de Académie',
      ],
      0,
      'Rousseau koos de Italiaanse zijde tegen Rameau. De twist over tekst versus muziek is de eerdere prima la musica-kwestie.',
    ],
    [
      'Wat offert "zuivere stemming" op ten opzichte van de gelijkzwevende temperatuur?',
      [
        'De mogelijkheid om vrijelijk te moduleren, omdat intervallen alleen in beperkte toonsoortrelaties zuiver zijn',
        'De zuiverheid van de reine kwint in elke toonsoort, die in verre toonsoorten hoorbaar te nauw wordt',
        'De mogelijkheid om überhaupt consonante tertsen te schrijven',
        'Een consistente octaaf, die enigszins variabel wordt',
      ],
      0,
      'Zuivere intervallen in de ene toonsoort betekenen wolfsintervallen in de andere. Gelijkzwevende temperatuur maakt alles een klein beetje verkeerd zodat alles even bruikbaar is.',
    ],
    [
      'Wat is een "Landini-cadens"?',
      [
        'Een cadens waarin de bovenstem naar de zesde toontrap daalt voordat hij naar het octaaf stijgt',
        'Een cadens waarin de bas een kwint naar de tonica valt',
        'Een plagale cadens gebruikt om een geestelijk werk af te sluiten, met de bas die van subdominant naar tonica valt',
        'Een cadens benaderd via een reeks parallelle sexten',
      ],
      0,
      'De ondertertscadens, alomtegenwoordig in het Italiaanse trecento en genoemd naar Landini in plaats van door hem uitgevonden.',
    ],
    [
      'Wat beweert het "sonateprincipe" in de zin van Rosen?',
      [
        'Materiaal dat buiten de tonica wordt gepresenteerd, moet later door hernieuwde presentatie binnen de tonica worden opgelost',
        'Een sonate moet precies twee contrasterende thema\'s bevatten',
        'De doorwerking mag geen nieuw thematisch materiaal introduceren',
        'De reprise moet de expositie zonder wijziging herhalen',
      ],
      0,
      'Het herformuleert de sonatevorm als een tonaal argument in plaats van een sjabloon, en dat is waarom het zoveel uitzonderingen op het sjabloon overleeft.',
    ],
    [
      'Hoe verschillen de gamelanstemmingen slendro en pelog?',
      [
        'Slendro verdeelt het octaaf in vijf ruwweg gelijke stappen; pelog in zeven ongelijke',
        'Slendro is een stemming en pelog een ritmische modus',
        'Slendro wordt alleen voor hofmuziek gebruikt en pelog alleen voor schaduwtheater',
        'Slendro is standaard vastgelegd en pelog varieert per ensemble',
      ],
      0,
      'In de praktijk is elk ensemble op zichzelf gestemd, dus de absolute toonhoogte varieert terwijl de intervalstructuur het systeem bepaalt.',
    ],
    [
      'Wat is "musique concrète" en wie gaf het die naam?',
      [
        'Compositie uit opgenomen geluid als materiaal, benoemd door Pierre Schaeffer',
        'Compositie uit elektronisch opgewekte tonen, benoemd door Herbert Eimert',
        'Compositie bepaald door toevalsprocedures, benoemd door John Cage',
        'Compositie voor band en live-uitvoerder, benoemd door Karlheinz Stockhausen',
      ],
      0,
      'Parijs tegenover Keulen: concreet geluid tegenover gesynthetiseerde toon, en de twee studio\'s waren het er een decennium over oneens.',
    ],
    [
      'Wat duidt een generaalbas-"6/4" boven een bastoon meestal aan in de achttiende-eeuwse praktijk?',
      [
        'Een akkoord in tweede omkering, meestal een cadentiële of doorgangsvorm eerder dan een zelfstandige harmonie',
        'Een akkoord in eerste omkering met een toegevoegde sext boven de bas, functionerend als subdominant-vervanger bij de cadens',
        'Een voorhouding boven de dominant die naar 5/3 oplost',
        'Een akkoord van de toegevoegde sext op de subdominant',
      ],
      0,
      'De cadentiële 6/4 is een dominant met verplaatste bovenstemmen, vandaar dat Schenkeriaanse analyse weigert het ooit een tonica te noemen.',
    ],
    [
      'Wat is het belang van de Notre-Dameschool voor de notatie?',
      [
        'Zij ontwikkelde de ritmische modi en de notatie waarmee die vastgelegd kon worden',
        'Zij introduceerde de vijflijnige notenbalk in zijn moderne vorm',
        'Zij noteerde muziek voor het eerst mensuraal in plaats van neumatisch',
        'Zij vestigde de boekdruk met losse letters voor polyfonie',
      ],
      0,
      'Léonin en Pérotin. Zodra duur specificeerbaar is, worden onafhankelijke ritmische stemmen mogelijk, en daaruit volgt alles verder.',
    ],
  ],
  professor: [
    [
      'Waarop draait de centrale stelling van Schenkers "Der freie Satz"?',
      [
        'Tonale werken werken één fundamentele structuur uit door hiërarchische niveaus van prolongatie',
        'Harmonische voortgang wordt bepaald door de akoestische eigenschappen van de boventoonreeks',
        'Vorm ontstaat uit thematische ontwikkeling los van tonale structuur',
        'Contrapunt en harmonie zijn gescheiden en onherleidbare systemen',
      ],
      0,
      'Reductieve analyse met een sterk normatieve inslag - en de polemiek over op welk repertoire dat toepasbaar is, is nooit gestopt.',
    ],
    [
      'Wat neemt "spectralisme" als compositorisch uitgangspunt?',
      [
        'De interne akoestische structuur van klank zelf als materiaal voor harmonie en vorm',
        'De wiskundige permutatie van toonhoogteklasseverzamelingen',
        'De stochastische verdeling van gebeurtenissen in de tijd',
        'Het gebruik van microtonale toonladders afgeleid van niet-westerse stemmingen',
      ],
      0,
      'Grisey en Murail, bij IRCAM. De analyse van een spectrum wordt het akkoord, waardoor klankkleur en harmonie geen te scheiden categorieën meer zijn.',
    ],
    [
      'Wat betoogde de "New Musicology" van de late jaren tachtig?',
      [
        'Dat muziek niet autonoom is',
        'Dat de muziektheorie de methoden van de cognitiewetenschap moet overnemen',
        'Dat de canon vervangen moet worden door populair repertoire',
        'Dat uitvoeringspraktijk de analytische categorieën moet bepalen',
      ],
      0,
      "McClary's Feminine Endings is het breekpunt. De autonomieclaim is wat werkelijk op het spel stond, meer dan enige specifieke interpretatie.",
    ],
    [
      'Waarnaar verschuift Lewins transformatietheorie de analytische vraag?',
      [
        'Van welke objecten een passage bevat naar welke operaties daartussen bewegen',
        'Van toonhoogte naar ritme als het primaire structuurbepalende parameter',
        'Van de partituur naar de opgenomen uitvoering als analyseobject',
        'Van het individuele werk naar het corpus als analyse-eenheid',
      ],
      0,
      'Generalized Musical Intervals and Transformations, 1987. Het gebaar, niet de inventarisatie - en de neo-Riemanniaanse analyse is wat daaruit gegroeid is.',
    ],
    [
      'Wat is het "probleem van de kwart" in de middeleeuwse en renaissancetheorie?',
      [
        'De reine kwart is qua verhouding consonant, maar werd in het contrapunt behandeld als oplossingsbehoevend',
        'De kwart kan in geen enkele destijds gebruikte temperatuur zuiver gestemd worden, in tegenstelling tot de kwint of het octaaf erboven',
        'De kwart ontbrak in het modale systeem vóór de vijftiende eeuw',
        'De kwart was verboden in parallelle beweging maar verplicht bij cadensen',
      ],
      0,
      'Een simpele verhouding die zich als dissonant gedraagt. Het is de standaardcasus voor de kloof tussen akoestische theorie en compositorische praktijk.',
    ],
    [
      'Wat bedoelt Adorno met de "cultuurindustrie" toegepast op muziek?',
      [
        'Gestandaardiseerde productie gepresenteerd als individuele keuze, waardoor luisteraars passieve herkenning aanleren',
        'De concentratie van uitgeverij en opname bij een handvol bedrijven',
        'De onderschikking van composities aan staatscultuurbeleid, zoals onder het socialistisch-realisme',
        'De vervanging van live-uitvoering door opgenomen reproductie',
      ],
      0,
      'Het argument van de pseudo-individualisering is het scherpste onderdeel, en ook het meest omstreden - het geeft luisteraars erg weinig krediet.',
    ],
    [
      'Hoe wordt de claim van de "Early Music"-beweging op historisch geïnformeerde uitvoering bekritiseerd?',
      [
        'Authenticiteit is onherwinbaar en de resulterende esthetiek is zelf een twintigste-eeuwse smaak',
        'Historische instrumenten kunnen niet met voldoende nauwkeurigheid gereconstrueerd worden',
        'Historische traktaten spreken elkaar op elk substantieel punt tegen',
        'Moderne luisteraars kunnen historische stemming niet als consonant horen',
      ],
      0,
      'Het argument van Taruskin: de beweging produceerde een modernistische klank - strak, snel, onsentimenteel - en noemde die geschiedenis.',
    ],
    [
      'Wat beschrijft "metrische dissonantie" in het analytische kader van Krebs?',
      [
        'Conflict tussen simultane of opeenvolgende metrische lagen binnen een passage',
        'Ritmische patronen die in geen enkele maatsoort te noteren zijn',
        'Een tempoverhouding die niet als eenvoudige verhouding uitdrukbaar is',
        'Accenten die consequent tegen de genoteerde maatstreep in vallen',
      ],
      0,
      'Groeperings- en verschuivingsdissonantie zijn de twee typen, en Schumann is het repertoire waarvoor het ontwikkeld werd.',
    ],
    [
      'Wat is de analytische moeilijkheid met "post-tonale" prolongatie?',
      [
        'Zonder een consonantie-dissonantiehiërarchie is er geen principiële basis om te zeggen welke gebeurtenissen structureel zijn',
        'Verzamelingsklasseanalyse kan geen passages van meer dan enkele maten aan',
        'Atonale werken herhalen materiaal zelden nauwkeurig genoeg om te vergelijken, waardoor motivische identiteit helemaal niet vastgesteld kan worden',
        'Seriële procedures bepalen elke gebeurtenis, waardoor er niets te analyseren overblijft',
      ],
      0,
      'Straus\' "problem of prolongation in post-tonal music" formuleert de voorwaarden helder, en concludeert grotendeels dat ze niet vervuld zijn.',
    ],
    [
      'Wat voegt de studie van "sound studies" toe aan de muziekwetenschap?',
      [
        'Ze behandelt luisteren zelf als historisch geconstrueerd, en breidt de analyse uit naar geruis, stilte en reproductie',
        'Ze meet de akoestische eigenschappen van historische uitvoeringsruimtes',
        'Ze past psychoakoestisch experiment toe op vragen over consonantie, en verankert esthetische categorieën in meetbare perceptie',
        'Ze documenteert bedreigde muziektradities via veldopnames',
      ],
      0,
      "Sternes werk over de geschiedenis van geluidsreproductie is het referentiepunt - het oor als een artefact in plaats van een constante.",
    ],
  ],
};

export const MORE_SCREEN: CategoryContent = {
  msc: [
    [
      'Wat is een "match on action"?',
      [
        'Een montage die een beweging begonnen in het vorige shot laat doorlopen',
        'Een montage tussen twee shots met overeenkomstige grafische compositie',
        'Een montage van de blik van een personage naar wat het ziet',
        'Een montage die dezelfde actie herhaalt vanuit een tweede hoek',
      ],
      0,
      'Van een blik naar het object daarvan monteren is de "eyeline match". Continuïteitsmontage steunt op beide, plus het aanhouden van één kant van de as.',
    ],
    [
      'Voor welk tijdschrift schreven de critici van de Franse nouvelle vague voornamelijk?',
      [
        'Cahiers du Cinéma',
        'Positif, de langdurige rivaal ervan',
        'Sight and Sound, uitgegeven in Londen',
        'Revue du Cinéma, de vooroorlogse voorganger ervan',
      ],
      0,
      'Bazin was medeoprichter in 1951; Truffaut, Godard, Rohmer en Chabrol schreven er allemaal voordat ze zelf gingen regisseren.',
    ],
    [
      'Wat omvat "mise-en-scène"?',
      [
        'Alles wat voor de camera is opgesteld: decor, belichting, kostuum, blocking',
        'De ordening van shots in de montage, en het ritme dat die ordening vestigt',
        'De relatie tussen beeld en geluidsspoor',
        'De beweging van de camera binnen een shot',
      ],
      0,
      'Of camerabeweging er wel of niet bij hoort, is in de literatuur een echt terminologisch meningsverschil.',
    ],
    [
      'Waardoor kenmerkt zich de Duitse expressionistische film?',
      [
        'Vervormde decors en gestileerde belichting die psychologische toestanden naar buiten brengen',
        'Locatieopnamen met niet-professionele acteurs',
        'Snelle montage opgebouwd volgens dialectische principes',
        'Lange shots met scherptediepte en minimale camerabeweging',
      ],
      0,
      'Caligari en Nosferatu. De andere drie beschrijven respectievelijk het neorealisme, de Sovjetmontage en het Baziniaanse realisme.',
    ],
    [
      'Wat is een "MacGuffin"?',
      [
        'Een plotmiddel dat de personages in beweging brengt terwijl zijn eigen aard er niet toe doet',
        'Een personage dat alleen wordt geïntroduceerd om in het eerste bedrijf gedood te worden',
        'Een visueel motief dat herhaald wordt om een komende onthulling aan te kondigen',
        'Een einde dat alles wat eraan voorafging herkadert',
      ],
      0,
      'De term van Hitchcock, en zijn eigen voorbeelden waren opzettelijk verwisselbaar - wat precies is wat de definitie beoogt.',
    ],
    [
      'Waarop had de Hays Code betrekking?',
      [
        'De morele inhoud van Amerikaanse studiofilms van de jaren 1930 tot de jaren 1960',
        'De handelspraktijken van distributeurs ten opzichte van exploitanten',
        'De tewerkstelling van buitenlanders in Hollywoodproducties',
        'De technische normen voor geluid-op-film-weergave',
      ],
      0,
      'Zelfregulering om wettelijke censuur te vermijden. De ineenstorting ervan in de jaren zestig leverde het ratingsysteem op dat de plaats innam.',
    ],
    [
      'Wat duidt de term "showrunner" aan in de televisie?',
      [
        'De hoofdschrijver-producent met creatieve zeggenschap over een serie',
        'De netwerkverantwoordelijke voor het bestellen van programma\'s',
        'Het hoofd van de productielogistiek voor een opnameschema',
        'De regisseur toegewezen om de visuele stijl van een serie te bepalen',
      ],
      0,
      'Schrijversgestuurd gezag is het structurele verschil tussen televisie en speelfilmproductie, en dat is waarom het auteursdebat daar anders verloopt.',
    ],
    [
      'Met welke productieomstandigheden wordt het Italiaanse neorealisme geassocieerd?',
      [
        'Locatieopnamen, niet-professionele acteurs en naoorlogse materiële schaarste',
        'De studioconstructie van gestileerde omgevingen',
        'Door de staat gefinancierde productie onder een gecentraliseerd filminstituut',
        'Multicamerawerk voor een snelle doorloop',
      ],
      0,
      'De schaarste was reëel, maar de esthetiek was een keuze: Cinecittà stond er nog en was deels bruikbaar.',
    ],
    [
      'Wat is "day for night"?',
      [
        'Opnemen bij daglicht met filters en onderbelichting om nacht te suggereren',
        'Opnemen in de schemering, voor de korte periode van bruikbaar omgevingslicht',
        'Interieurs overdag opnemen en exterieurs \'s nachts',
        'Een daglichtnegatief afdrukken op hoogcontrast-materiaal',
      ],
      0,
      'La nuit américaine in het Frans, wat de titel van Truffaut is en tegelijk een treffende uitspraak over waar de techniek geïndustrialiseerd werd.',
    ],
    [
      'Wat adviseert "de dertig-gradenregel"?',
      [
        'De camerahoek tussen opeenvolgende shots van hetzelfde onderwerp met minstens dertig graden veranderen',
        'De camera binnen dertig graden van de kijkrichtingsas houden',
        'Pans beperken tot dertig graden per seconde om stroboscopie te vermijden',
        'Sleutellichten op dertig graden ten opzichte van het gezicht plaatsen, gemeten vanaf de lensas op ooghoogte',
      ],
      0,
      'Minder dan dat leest als een jumpcut - een fout in plaats van een keuze, tenzij het juist de bedoelde keuze is.',
    ],
  ],
  phd: [
    [
      'Wat beoogt Metz\' "grande syntagmatique"?',
      [
        'Een taxonomie van de manieren waarop shots tot narratieve segmenten geordend kunnen worden',
        'Een grammatica die shots met woorden en sequenties met zinnen gelijkstelt',
        'Een theorie van toeschouwersidentificatie afgeleid van Lacan',
        'Een classificatie van genres naar hun narratieve economie',
      ],
      0,
      'Het nut ervan is omstreden en de ambitie is precies het punt: filmsemiotiek die eenheden boven het niveau van het shot proberen te vinden.',
    ],
    [
      'Wat is "suture" in de filmtheorie?',
      [
        'Het proces waardoor shot-tegenshot de toeschouwer inhecht in het kijksysteem van de film',
        'Het naadloos aan elkaar lassen van twee negatieven in het optisch printen, zodat de lasnaad op het scherm onzichtbaar is',
        'De afstemming van het geluidsspoor op het beeld bij de mix',
        'De narratieve oplossing van een ambiguïteit die eerder open was gelaten',
      ],
      0,
      'Oudart en Dayan, uit Lacan. Het is waar de theorie van de jaren zeventig ideologie situeert - in de montagestructuur eerder dan in de inhoud.',
    ],
    [
      'Wat betoogt Mulvey in "Visual Pleasure and Narrative Cinema"?',
      [
        'De klassieke cinema structureert het kijken als actief en mannelijk',
        'Vrouwelijke toeschouwers kunnen zich helemaal niet identificeren met klassieke vertelling',
        'Scopofilie is een universele in plaats van historisch specifieke structuur',
        'De genoegens van de cinema zijn onlosmakelijk verbonden met haar commerciële vorm',
      ],
      0,
      '1975. Mulvey herzag zelf later haar behandeling van de vrouwelijke toeschouwer, iets wat de ontvangst van het essay vaak vergeet.',
    ],
    [
      'Wat is "haptische visualiteit" zoals Marks het gebruikt?',
      [
        'Een beeld dat uitnodigt tot een aanraakachtige, nabije, texturele perceptie in plaats van optische beheersing',
        'Het gebruik van tastbare props om een belichaamde reactie op te wekken',
        'Het projecteren van film op onregelmatige oppervlakken, waardoor het beeld de textuur van zijn drager overneemt',
        'De overeenkomst tussen camerabeweging en lichamelijke beweging',
      ],
      0,
      'Ontwikkeld voor interculturele en diasporische cinema, waar herinnering vaak eerder in textuur dan in verhaal wordt gedragen.',
    ],
    [
      'Wat bracht Kracauer van de Frankfurter Schule in "Theory of Film" naar voren?',
      [
        'Een realistisch argument dat de roeping van de film de verlossing van de fysieke realiteit is',
        'Een kritiek op film als het exemplarische product van de cultuurindustrie',
        'Een verklaring van het cinematografische apparaat als ideologische machine',
        'Een fenomenologie van de belichaamde waarneming van de toeschouwer',
      ],
      0,
      'Het staat dichter bij Bazin dan bij Adorno, wat mensen verrast die hem indelen naar instituut in plaats van naar argument.',
    ],
    [
      'Wat is "het apparaat" in de opvatting van Baudry?',
      [
        'De hele projectiesituatie',
        'De camera en de lens als instrumenten die het Renaissanceperspectief opleggen',
        'De arbeidsverdeling van het studiosysteem',
        'Het distributienetwerk dat bepaalt wat gezien kan worden',
      ],
      0,
      "De grotanalogie van Plato doet het zware werk, en het essay is even zeer een psychoanalyse van de bioscoopganger als filmtheorie.",
    ],
    [
      'Wat beschrijft "cinema of attractions"?',
      [
        'De aanspreekvorm van de vroege film op de toeschouwer via vertoon en spektakel in plaats van narratieve absorptie',
        'Het gebruik van sterren als voornaamste publiekstrekker',
        'De kermis- en variétézalen waarin films voor het eerst vertoond werden',
        'Truckfilms mogelijk gemaakt door stopsubstitutie, en de kermisvertoningscircuits die ze verspreidden',
      ],
      0,
      'De term van Gunning. De kracht ervan is dat het de vroege film tot iets anders maakt dan onbeholpen vertelling.',
    ],
    [
      'Wat wordt het "180-gradensysteem" verweten ideologisch te bewerkstelligen?',
      [
        'Het naturaliseert een coherente, beheersbare ruimte en verhult zo de gemaaktheid van de scène',
        'Het beperkt het aantal beschikbare camerastandpunten en verlaagt zo de productiekosten',
        'Het geeft dialoog voorrang boven actie in de opbouw van scènes',
        'Het vereist continuïteit van belichting tussen opnamen',
      ],
      0,
      'De kritiek van de jaren zeventig behandelt de klassieke stijl als een uitspraak over kennis, niet louter als een verzameling ambachtelijke conventies.',
    ],
    [
      'Wat bedoelt Bordwell met "historical poetics" van de film?',
      [
        'Bestuderen hoe films geconstrueerd zijn en hoe die normen veranderen, in plaats van interpretatieve theorie',
        'De historische receptie van films reconstrueren uit persarchieven',
        'Literaire poëtica toepassen op scenariostructuur',
        'Films dateren aan hun technische kenmerken, op basis van filmmateriaal, perforatie en geluidsformaat',
      ],
      0,
      'Het is expliciet een weerlegging van de Grote Theorie - problemen en normen in plaats van doctrine, en zelfs zijn eigen critici noemen het formalisme met een zuiver geweten.',
    ],
    [
      'Wat is het belang van de "politique des auteurs" van de nouvelle vague?',
      [
        'Ze stelde dat de regisseur de auteur van een film is',
        'Ze eiste staatssubsidie voor debuterende regisseurs',
        'Ze verwierp literaire bewerkingen ten gunste van originele scenario\'s',
        'Ze hield vast aan het idee dat kritiek aan elke praktische filmproductie voorafgaat',
      ],
      0,
      'Sarris nam de term over in het Engels als "auteur theory", en de misvertaling van een polemische stellingname naar een theorie is zelf onderdeel van de geschiedenis.',
    ],
  ],
  professor: [
    [
      'Waarop draait Deleuzes onderscheid tussen het bewegingsbeeld en het tijdsbeeld?',
      [
        'Of tijd indirect wordt gepresenteerd door actie, of rechtstreeks, losgemaakt van de sensomotorische continuïteit',
        'Of een film continuïteitsmontage of montage gebruikt',
        'Of het vertelperspectief subjectief of objectief is',
        'Of de camera binnen het shot beweeglijk of statisch is, en of die beweging door de actie gemotiveerd is',
      ],
      0,
      'De breuk wordt gesitueerd in de naoorlogse cinema, met het neorealisme als scharnier - een filosofische periodisering, geen stilistische.',
    ],
    [
      'Waarover gaat de "dispositif"-wending in recente beeldmediastudies?',
      [
        'De inrichting van schermen, ruimtes en gewoontes waardoor bewegende beelden ontmoet worden',
        'De apparatuur op de set en het effect daarvan op de stijl, van lenskeuze tot camerasteun',
        'De juridische kaders die distributierechten regelen',
        'De ordening van shots binnen één sequentie',
      ],
      0,
      'Het is de manier waarop het vakgebied telefoons, galerieën en streaming heeft opgenomen zonder te doen alsof het gewoon meer bioscoop is.',
    ],
    [
      'Wat stelt het "post-cinematische" argument?',
      [
        'Digitale productie en distributie hebben de ontologie van het medium veranderd',
        'De bioscoopbezoek is teruggelopen tot het punt van culturele irrelevantie',
        'Digitale beelden zijn in principe niet te onderscheiden van fotografische beelden',
        'De filmtheorie moet vervangen worden door mediaarcheologie',
      ],
      0,
      'De indexicale band met een profilmische gebeurtenis is wat verloren zou zijn - Rodowick en Shaviro voeren het argument, en Baziniaanse critici zijn niet overtuigd.',
    ],
    [
      'Wat stelde het essay "The Third Meaning" van Barthes over het filmstill?',
      [
        'Een betekenisniveau voorbij het informatieve en het symbolische, weerbarstig tegen omschrijving',
        'Dat stills films verkeerd voorstellen door de beweging stil te leggen, waardoor geen still enige filmische betekenis kan dragen',
        'Dat publiciteitsbeelden bepalen hoe een film ontvangen wordt',
        'Dat montage betekenis produceert die in geen enkel afzonderlijk beeld aanwezig is',
      ],
      0,
      'Geschreven over stills van Eisenstein. De obtuse betekenis is de voorloper van wat hij later in de fotografie het punctum noemde.',
    ],
    [
      'Wat is het methodologische probleem met "screen memory" in de wetenschap over trauma en film?',
      [
        'Het leent een klinisch begrip voor culturele analyse zonder de bewijsvoorwaarden die het klinische gebruik veronderstelt',
        'Trauma kan in geen enkel medium worden weergegeven, waardoor het object leeg blijft',
        'Getuigenis en fictie zijn niet te onderscheiden in archiefbeelden, waardoor herkomstonderzoek een voorwaarde is voor elke lezing',
        'Het begrip geldt alleen voor individuele en nooit voor collectieve herinnering',
      ],
      0,
      'Geen argument tegen het werk, maar de reden dat het goede werk op dit terrein zo voorzichtig is met wat het claimt te hebben aangetoond.',
    ],
    [
      'Wat draagt onderzoek naar "attention economies" bij aan de televisiewetenschap?',
      [
        'Het herkadert programmering, serialiteit en platformaanbeveling als concurrenten om een schaarse hulpbron',
        'Het meet de omvang van het publiek nauwkeuriger dan kijkcijferpanels',
        'Het verklaart genrecycli alleen uit productiekosten, en behandelt publieksvoorkeur als vrijwel constant',
        'Het behandelt kijkers als rationele consumenten van narratieve goederen',
      ],
      0,
      'Het is waarom bingerelease en episodische programmering leesbaar worden als verschillende weddenschappen op dezelfde schaarste, in plaats van als eenvoudige technische opvolgers van elkaar.',
    ],
    [
      'Wat staat op het spel in het debat over "quality television" als categorie?',
      [
        'Of het label formele eigenschappen beschrijft of gewoon bepaalde smaken en doelgroepen legitimeert',
        'Of televisie auteurschap in de cinematografische zin kan dragen',
        'Of seriële vertelling inherent superieur is aan episodische, en of dat onderscheid iets formeels raakt',
        'Of abonnementsfinanciering beter schrijfwerk oplevert dan reclamefinanciering',
      ],
      0,
      'Newman en Levines "Legitimating Television" is de sceptische lezing, en die draait grotendeels om klasse en gender in plaats van om vakmanschap.',
    ],
    [
      'Wat benadrukt het begrip "media convergence" zoals Jenkins het gebruikt?',
      [
        'Culturele en participatieve verschuivingen in hoe publiek content laat circuleren, niet louter de technologische versmelting van apparaten',
        'De concentratie van mediabezit in minder concerns',
        'De unificatie van transmissienormen over platforms heen',
        'De vervanging van uitzending door on-demand distributie, en de programmeringspraktijken die daaruit volgden',
      ],
      0,
      'Het onderscheid met het eigendomsverhaal is van belang: Jenkins beschrijft publieksarbeid, en de politiek-economische kritiek is dat dit onderschat wie de waarde daarvan afroomt.',
    ],
    [
      'Hoe veranderde de archiefwending de filmgeschiedschrijving?',
      [
        'Toegang tot niet-canonieke kopieën en papieren dossiers verdrong verhalen die op de overgebleven canon gebouwd waren',
        'Het vestigde digitale restauratie als de standaard wetenschappelijke editie',
        'Het verlegde de aandacht uitsluitend van productie naar receptie',
        'Het bewees dat de meeste stomme films in enige vorm bewaard zijn, tegen gangbare schattingen van de omvang van het verlies in',
      ],
      0,
      'Het FIAF-congres van Brighton in 1978 is het gebruikelijke ijkpunt. Ruwweg driekwart van de stomme-filmproductie is verloren gegaan, en dat is precies het punt over canonvorming.',
    ],
    [
      'Welk probleem deelt "world cinema" als categorie met "world literature"?',
      [
        'Het loopt het risico alles te definiëren door zijn afstand tot een metropolitisch centrum dat zelf ongemarkeerd blijft',
        'Het kan geen co-producties met meerdere nationale oorsprongen accommoderen',
        'Het hangt af van festivalprogrammering, wat geen wetenschappelijk bewijs is',
        'Het sluit documentaire en experimentele praktijk per definitie uit',
      ],
      0,
      'Het probleem van de restcategorie: "world" komt neer op "niet-Hollywood", wat iets zegt over de classificeerder in plaats van over de films.',
    ],
  ],
};
